import {
  allManageAppsRoutes,
  allManageAppVersionRoutes,
  allManagePartnersRoutes,
  appVersionPublishFormRoute,
  appVersionPublishRoute,
  manageAppsBaseRoute,
  manageAppVersionBaseRoute,
  managePartnersBaseRoute,
} from '@atlassian/mpac-partners';
import { AtlassianSupportsUkraineRouteHandler } from '@atlassian/mpac-primitives';

import AgentsPageRouteHandler from '../pages/agents/AgentsPageRouteHandler';
import AppBundleDetailRouteHandler from '../pages/appBundles/AppBundleDetailRouteHandler';
import AppBundlesListRouteHandler from '../pages/appBundles/AppBundlesListRouteHandler';
import ArchivedPluginDetailsRouteHandler from '../pages/archivedPluginDetails/ArchivedPluginDetailsRouteHandler';
import ArchivedVendorsRouteHandler from '../pages/archivedVendors/ArchivedVendorsRouteHandler';
import BundlePageRouteHandler from '../pages/bundles/BundlePageRouteHandler';
import NotFoundPage from '../pages/errors/NotFoundPage';
import UserProfileRouteHandler from '../pages/userProfile/UserProfileRouteHandler';
import { VersionRedirectRoute } from '../pages/versionHistory/RedirectRoute';
import VersionHistoryPageRouteHandler from '../pages/versionHistory/VersionHistoryPageRouteHandler';
import AdminPageRouteHandler from './components/AdminPageRoutes/AdminPageRouteHandler';
import resolveAppListingRoutes from './components/AppListingRoutes/components/AppListingRoutes';
import {
  ManageAppCreateRouteHandler,
  ManageAppsRouteHandler,
  ManageAppVersionRouteHandler,
  ManagePartnersRouteHandler,
  ManageVendorCreateRouteHandler,
  PublishAppVersionRoutesHandler,
} from './components/PartnerRoutes';
import resolveVendorDetailsRoutes from './components/VendorDetailsRoutes/VendorDetailsRoutes';
import { getAetherRoutes } from './getAetherRoutes';
import { StaticRedirectRoute, staticRedirectRoutesMap } from './StaticRedirectRoute';

import type { AMKTRoute } from './components/Routes.types';

type GetAllRoutesArgs = {
  flags: {
    enableAppBundles: boolean;
    enableStaticBundlePages: boolean;
    enableDevopsEssentialsBundlePage: boolean;
    enableHomepageSpotlight: boolean;
    enableMpacAgentsPageView: boolean;
    enableManageVendorCreatePageView: boolean;
    enableManageAppCreatePageView: boolean;
    archivedVendorPageView: boolean;
  };
  isMobile?: boolean;
  isAdmin?: boolean;
};

function getStaticRedirectRoutes(): AMKTRoute {
  return Object.keys(staticRedirectRoutesMap).map((path) => {
    return {
      name: staticRedirectRoutesMap[path].screen,
      path,
      exact: true,
      component: StaticRedirectRoute,
    };
  });
}

function getAppBundlesRoutes(): AMKTRoute[] {
  return [
    {
      name: 'appBundlesListingScreen',
      path: '/app-bundles',
      exact: true,
      component: AppBundlesListRouteHandler,
      showAetherNavAndFooter: true,
    },
    {
      name: 'appBundleDetailsScreen',
      path: '/app-bundles/:appBundleId',
      exact: true,
      component: AppBundleDetailRouteHandler,
      showAetherNavAndFooter: true,
    },
  ];
}

function getAdminRoutes(): AMKTRoute[] {
  return [
    {
      name: 'adminScreen',
      path: '/admin/configure',
      exact: true,
      component: AdminPageRouteHandler,
      showAetherNavAndFooter: true,
    },
  ];
}

function getStaticBundlePagesRoutes(): AMKTRoute[] {
  return [
    {
      exact: true,
      name: 'bundleScreen',
      path: '/bundles/:bundleId',
      component: BundlePageRouteHandler,
      hidePartnerLinks: true,
      showAetherNavAndFooter: true,
    },
  ];
}

function getMpacAgentsRoutes(): AMKTRoute[] {
  return [
    {
      exact: true,
      name: 'AgentsScreen',
      path: '/ai-hub',
      component: AgentsPageRouteHandler,
      hidePartnerLinks: true,
      showAetherNavAndFooter: true,
    },
  ];
}

function getManageVendorCreateRoutes(): AMKTRoute[] {
  return [
    {
      exact: true,
      name: 'manageVendorCreateScreen',
      path: '/manage/vendor/create',
      component: ManageVendorCreateRouteHandler,
      hidePartnerLinks: true,
      showAetherNavAndFooter: false,
    },
  ];
}

function getManageAppCreateRoutes(): AMKTRoute[] {
  return [
    {
      exact: true,
      name: 'manageAppCreateScreen',
      path: '/manage/apps/create',
      component: ManageAppCreateRouteHandler,
      hidePartnerLinks: true,
      showAetherNavAndFooter: false,
    },
  ];
}

function getUserProfileRoutes(): AMKTRoute[] {
  return [
    {
      name: 'userProfileScreen',
      path: '/users/:aaid',
      exact: true,
      component: UserProfileRouteHandler,
    },
  ];
}

// eslint-disable-next-line max-lines-per-function
export default function getAllRoutes({
  flags: {
    enableAppBundles,
    enableStaticBundlePages,
    enableDevopsEssentialsBundlePage,
    enableHomepageSpotlight,
    enableMpacAgentsPageView,
    enableManageVendorCreatePageView,
    archivedVendorPageView,
    enableManageAppCreatePageView,
  },
  isMobile,
}: GetAllRoutesArgs): AMKTRoute[] {
  let routes: AMKTRoute[] = [];

  routes = getAetherRoutes(enableHomepageSpotlight, isMobile || false);

  if (enableAppBundles) {
    routes = [...routes, ...getAppBundlesRoutes()];
  }

  if (enableStaticBundlePages || enableDevopsEssentialsBundlePage) {
    routes = [...routes, ...getStaticBundlePagesRoutes()];
  }
  if (enableMpacAgentsPageView) {
    routes = [...routes, ...getMpacAgentsRoutes()];
  }
  if (enableManageVendorCreatePageView) {
    routes = [...routes, ...getManageVendorCreateRoutes()];
  }
  if (enableManageAppCreatePageView) {
    routes = [...routes, ...getManageAppCreateRoutes()];
  }

  return [
    ...routes,
    ...resolveVendorDetailsRoutes(),
    ...resolveAppListingRoutes(),
    ...getUserProfileRoutes(),
    {
      name: 'managePartnersScreen',
      path: managePartnersBaseRoute,
      component: ManagePartnersRouteHandler,
    },
    {
      name: 'ukraineSupportScreen',
      path: '/atlassian-stands-with-ukraine',
      exact: true,
      component: AtlassianSupportsUkraineRouteHandler,
    },
    {
      name: 'publishNewPrivateAppVersion',
      path: appVersionPublishRoute.path,
      component: PublishAppVersionRoutesHandler,
      exact: true,
    },
    {
      name: 'publishNewPrivateAppVersion',
      path: appVersionPublishFormRoute.path,
      component: PublishAppVersionRoutesHandler,
      exact: true,
    },
    {
      name: 'manageAppVersionScreen',
      path: manageAppVersionBaseRoute,
      component: ManageAppVersionRouteHandler,
    },
    {
      name: 'manageAppsScreen',
      path: manageAppsBaseRoute,
      component: ManageAppsRouteHandler,
    },
    {
      name: 'versionHistoryScreen',
      path: '/apps/:appId(\\d+)/:appSlug([^/]+)?/version-history',
      component: VersionHistoryPageRouteHandler,
      showAetherNavAndFooter: true,
    },
    {
      name: 'versionHistoryScreen',
      path: '/plugins/:appKey([^/]+)/version/:buildNumber',
      component: VersionRedirectRoute,
      showAetherNavAndFooter: true,
    },
    {
      name: 'archivedPluginDetailsScreen',
      path: '/archive/:appId(\\d+)',
      component: ArchivedPluginDetailsRouteHandler,
      showAetherNavAndFooter: true,
    },
    ...(archivedVendorPageView
      ? [
          {
            name: 'archivedVendorsScreen',
            path: '/archive/vendors/:partnerId(\\d+)',
            component: ArchivedVendorsRouteHandler,
            showAetherNavAndFooter: true,
          },
        ]
      : []),
    ...getStaticRedirectRoutes(),
    ...getAdminRoutes(),
    {
      name: 'notFoundScreen',
      // eslint-disable-next-line react/display-name
      render: (routeProps) => {
        return <NotFoundPage {...routeProps} />;
      },
      showAetherNavAndFooter: true,
    },
    ...allManagePartnersRoutes,
    ...allManageAppsRoutes,
    ...allManageAppVersionRoutes,
  ];
}
